import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Popup, Source } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import {
  GeoPoint,
  addDoc,
  collection,
  deleteDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  doc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useMapboxToken } from './MapboxTokenContext';
import { useKnownSwimResearch } from './KnownSwimResearchContext';
import { useRecentSwims } from './RecentSwimsContext';
import { useKnownSwims } from './KnownSwimsContext';
import AreaResearchPanel, { editableAreaResearch, emptyAreaResearch, normalizeAreaResearch } from './AreaResearchPanel';
import 'mapbox-gl/dist/mapbox-gl.css';

const BROWSE_ZOOM = 10;
const EDITOR_ZOOM = 12;
const EDITOR_STYLE = 'mapbox://styles/visskiss/cms9h5uii004o01rd1aho7v4w';

const emptyFeatureCollection = { type: 'FeatureCollection', features: [] };
const defaultViewState = { longitude: -98.5795, latitude: 39.8283, zoom: 3 };

const viewStateFromMap = (map) => {
  const center = map.getCenter();
  return {
    longitude: center.lng,
    latitude: center.lat,
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch()
  };
};

const boundsFromMap = (map) => {
  const bounds = map.getBounds();
  return {
    minLat: bounds.getSouth(),
    maxLat: bounds.getNorth(),
    minLng: bounds.getWest(),
    maxLng: bounds.getEast()
  };
};

const calculateBoundingBox = (route) => {
  if (route.length === 0) return null;

  return route.reduce((box, point) => ({
    minLat: Math.min(box.minLat, point.latitude),
    maxLat: Math.max(box.maxLat, point.latitude),
    minLng: Math.min(box.minLng, point.longitude),
    maxLng: Math.max(box.maxLng, point.longitude)
  }), {
    minLat: route[0].latitude,
    maxLat: route[0].latitude,
    minLng: route[0].longitude,
    maxLng: route[0].longitude
  });
};

const calculateRouteDistanceMeters = (route) => {
  const earthRadiusMeters = 6371000;
  let distance = 0;

  for (let index = 1; index < route.length; index += 1) {
    const previous = route[index - 1];
    const current = route[index];
    const latitudeDelta = (current.latitude - previous.latitude) * Math.PI / 180;
    const longitudeDelta = (current.longitude - previous.longitude) * Math.PI / 180;
    const latitude1 = previous.latitude * Math.PI / 180;
    const latitude2 = current.latitude * Math.PI / 180;
    const haversine = Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;

    distance += 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  return distance;
};

const formatDistance = (distanceInMeters) =>
  distanceInMeters >= 1000
    ? `${(distanceInMeters / 1000).toFixed(2)} km`
    : `${Math.round(distanceInMeters)} m`;

const calculateCoordinateRouteDistanceMeters = (coordinates) => {
  const earthRadiusMeters = 6371000;
  let distance = 0;

  for (let index = 1; index < coordinates.length; index += 1) {
    const [previousLongitude, previousLatitude] = coordinates[index - 1];
    const [longitude, latitude] = coordinates[index];
    const latitudeDelta = (latitude - previousLatitude) * Math.PI / 180;
    const longitudeDelta = (longitude - previousLongitude) * Math.PI / 180;
    const latitude1 = previousLatitude * Math.PI / 180;
    const latitude2 = latitude * Math.PI / 180;
    const haversine = Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;

    distance += 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  return distance;
};

const formatRouteMonthYear = (startDate) => {
  const date = startDate ? new Date(startDate) : null;
  return date && !Number.isNaN(date.valueOf())
    ? new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
    : null;
};

const formatCoordinate = ({ latitude, longitude }) =>
  `[${latitude.toFixed(5)},${longitude.toFixed(5)}]`;

const aiModeSearchUrl = (query) =>
  `https://www.google.com/search?udm=50&q=${encodeURIComponent(query)}`;

const mapLabelContextAtPoint = (mapRef, event) => {
  let features = [];
  try {
    const map = mapRef.current?.getMap?.() || mapRef.current;
    const { x, y } = event.point;
    features = map?.queryRenderedFeatures?.([
      [x - 12, y - 12],
      [x + 12, y + 12]
    ]) || [];
  } catch (error) {
    console.error('Unable to query rendered map labels:', error);
  }
  const namedFeatures = features.filter((feature) => {
    const properties = feature.properties || {};
    return typeof (
      properties.name_en ||
      properties.name ||
      properties['name:en'] ||
      properties.name_int ||
      properties.name_local
    ) === 'string';
  });
  const placeFeature = namedFeatures.find((feature) => {
    const properties = feature.properties || {};
    return properties.group || properties.class || properties.maki || feature.layer?.type === 'symbol';
  }) || namedFeatures[0];
  const properties = placeFeature?.properties || {};
  return {
    placeName: properties.name_en ||
      properties.name ||
      properties['name:en'] ||
      properties.name_int ||
      properties.name_local ||
      null
  };
};

const routeIntersectsBounds = (route, bounds) => {
  const box = route.geoBoundingBox;
  return box &&
    box.maxLat >= bounds.minLat &&
    box.minLat <= bounds.maxLat &&
    box.maxLng >= bounds.minLng &&
    box.minLng <= bounds.maxLng;
};

const routeFeature = (swim) => ({
  type: 'Feature',
  properties: { id: swim.id, name: swim.name },
  geometry: {
    type: 'LineString',
    coordinates: (swim.route || []).map((point) => [point.longitude, point.latitude])
  }
});

const lineLayout = { 'line-join': 'round', 'line-cap': 'round' };

const KnownSwimsPage = ({ initialSwim, onInitialSwimHandled, initialSelectedSwim, onInitialSelectedSwimHandled, focusRequest, onFocusHandled }) => {
  const mapboxToken = useMapboxToken();
  const browseMapRef = useRef(null);
  const editorMapRef = useRef(null);
  const moveTimeoutRef = useRef(null);
  const fittedDraftIdRef = useRef(null);
  const activeEditorSwimIdRef = useRef(null);
  const initialSwimHandledRef = useRef(false);
  const initialSelectedSwimHandledRef = useRef(false);
  const draftNameRef = useRef('');
  const selectedSwimIdRef = useRef(null);
  const focusRequestHandledRef = useRef(null);
  const [visibleSwims, setVisibleSwims] = useState([]);
  const [selectedSwim, setSelectedSwim] = useState(null);
  const [selectedAreaResearch, setSelectedAreaResearch] = useState(null);
  const [loadingSelectedAreaResearch, setLoadingSelectedAreaResearch] = useState(false);
  const [mode, setMode] = useState('browse');
  const [browseView, setBrowseView] = useState({ ...defaultViewState, zoom: 1.5 });
  const [editorView, setEditorView] = useState(defaultViewState);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [selectedPointIndex, setSelectedPointIndex] = useState(null);
  const [editAction, setEditAction] = useState(null);
  const [userRoutes, setUserRoutes] = useState([]);
  const [selectedUserRouteIndex, setSelectedUserRouteIndex] = useState(0);
  const [loadingUserRoutes, setLoadingUserRoutes] = useState(false);
  const [areaResearch, setAreaResearch] = useState(null);
  const [loadingAreaResearch, setLoadingAreaResearch] = useState(false);
  const [editingAreaResearch, setEditingAreaResearch] = useState(false);
  const [savingAreaResearch, setSavingAreaResearch] = useState(false);
  const [editorZoom, setEditorZoom] = useState(3);
  const [saving, setSaving] = useState(false);
  const [coordinateMenu, setCoordinateMenu] = useState(null);
  const [focusedLatestRoute, setFocusedLatestRoute] = useState([]);
  const [focusedLatestSwim, setFocusedLatestSwim] = useState(null);
  const [pendingFocusedRouteLoad, setPendingFocusedRouteLoad] = useState(false);
  const { researchSwim, isResearching, researchError } = useKnownSwimResearch();
  const { swims: recentSwims, loadRecentSwims } = useRecentSwims();
  const {
    knownSwims,
    loading: loadingKnownSwims,
    error: knownSwimsError,
    ensureKnownSwims
  } = useKnownSwims();
  const researching = isResearching(draft?.id);
  const activeResearchError = researchError(draft?.id);

  const refreshVisibleSwims = useCallback(() => {
    const map = browseMapRef.current;
    if (!map || map.getZoom() < BROWSE_ZOOM) {
      setVisibleSwims([]);
      return;
    }
    const bounds = boundsFromMap(map);
    setVisibleSwims(knownSwims.filter((swim) => routeIntersectsBounds(swim, bounds)));
  }, [knownSwims]);

  useEffect(() => {
    ensureKnownSwims();
  }, [ensureKnownSwims]);

  useEffect(() => {
    void loadRecentSwims();
  }, [loadRecentSwims]);

  useEffect(() => {
    draftNameRef.current = draftName;
  }, [draftName]);

  useEffect(() => {
    refreshVisibleSwims();
  }, [refreshVisibleSwims]);

  useEffect(() => () => {
    clearTimeout(moveTimeoutRef.current);
    activeEditorSwimIdRef.current = null;
  }, []);

  const knownRouteFeatures = useMemo(() => ({
    type: 'FeatureCollection',
    features: visibleSwims
      .filter((swim) => swim.route?.length >= 2)
      .map(routeFeature)
  }), [visibleSwims]);

  const recentRouteFeatures = useMemo(() => ({
    type: 'FeatureCollection',
    features: recentSwims
      .filter((swim) => swim.route?.length >= 2)
      .map((swim) => ({
        type: 'Feature',
        properties: { id: swim.id },
        geometry: { type: 'LineString', coordinates: swim.route }
      }))
  }), [recentSwims]);

  const focusedLatestRouteFeature = useMemo(() => ({
    type: 'FeatureCollection',
    features: focusedLatestRoute.length >= 2 ? [{
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: focusedLatestRoute }
    }] : []
  }), [focusedLatestRoute]);

  const startEditing = (swim = null) => {
    const nextEditorView = browseMapRef.current ? viewStateFromMap(browseMapRef.current) : browseView;
    const hasPrefetchedResearch = Boolean(swim) && Object.prototype.hasOwnProperty.call(swim, 'areaResearch');
    setDraft(swim ? { ...swim, route: swim.route || [] } : { route: [] });
    setDraftName(swim?.name || '');
    setSelectedPointIndex(null);
    setEditAction(null);
    setEditorView(nextEditorView);
    setEditorZoom(nextEditorView.zoom);
    activeEditorSwimIdRef.current = swim?.id || null;
    setAreaResearch(hasPrefetchedResearch ? swim.areaResearch : null);
    setLoadingAreaResearch(false);
    setEditingAreaResearch(false);
    setCoordinateMenu(null);
    setMode('edit');
    if (swim?.id && hasPrefetchedResearch && !swim.areaResearch) {
      void runAreaResearch(false, false, swim);
    } else if (swim?.id && !hasPrefetchedResearch) {
      loadAreaResearch(swim.id);
    }
  };

  useEffect(() => {
    if (!initialSwim || initialSwimHandledRef.current) return;
    initialSwimHandledRef.current = true;
    startEditing(initialSwim);
    onInitialSwimHandled?.();
    // initialSwim is a one-time routing instruction, not an editor-state dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSwim, onInitialSwimHandled]);

  useEffect(() => {
    if (!initialSelectedSwim || initialSelectedSwimHandledRef.current || mode !== 'browse' || !mapLoaded || !browseMapRef.current) return;

    initialSelectedSwimHandledRef.current = true;
    selectedSwimIdRef.current = initialSelectedSwim.id;
    setSelectedSwim(initialSelectedSwim);
    setSelectedAreaResearch(null);
    loadSelectedAreaResearch(initialSelectedSwim.id);

    const route = initialSelectedSwim.route || [];
    if (route.length >= 2) {
      const bounds = new mapboxgl.LngLatBounds();
      route.forEach((point) => bounds.extend([point.longitude, point.latitude]));
      browseMapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 0 });
    }
    onInitialSelectedSwimHandled?.();
    // initialSelectedSwim is a one-time browse-map routing instruction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedSwim, mapLoaded, mode, onInitialSelectedSwimHandled]);

  useEffect(() => {
    if (!focusRequest || mode !== 'browse' || !mapLoaded || !browseMapRef.current) return;
    const focusKey = focusRequest.key || `${focusRequest.timezone}-${focusRequest.coordinates.length}`;
    if (focusRequestHandledRef.current === focusKey) return;

    const coordinates = focusRequest.coordinates;
    setFocusedLatestRoute(
      Array.isArray(focusRequest.route) && focusRequest.route.length >= 2
        ? focusRequest.route
        : []
    );
    setFocusedLatestSwim(
      typeof focusRequest.pinnedSwim?.userID === 'string' && typeof focusRequest.pinnedSwim?.swimID === 'string'
        ? focusRequest.pinnedSwim
        : null
    );
    setPendingFocusedRouteLoad(true);
    if (coordinates.length === 1) {
      browseMapRef.current.flyTo({
        center: [coordinates[0].longitude, coordinates[0].latitude],
        zoom: 11,
        duration: 0
      });
    } else if (coordinates.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach((coordinate) => bounds.extend([coordinate.longitude, coordinate.latitude]));
      browseMapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 0 });
    }
    focusRequestHandledRef.current = focusKey;
    onFocusHandled?.();
  }, [focusRequest, mapLoaded, mode, onFocusHandled]);

  const closeOverlay = () => {
    selectedSwimIdRef.current = null;
    setSelectedSwim(null);
    setSelectedAreaResearch(null);
    setLoadingSelectedAreaResearch(false);
  };

  const returnToBrowse = () => {
    if (editorMapRef.current) {
      setBrowseView(viewStateFromMap(editorMapRef.current));
    }
    activeEditorSwimIdRef.current = null;
    setLoadingAreaResearch(false);
    setCoordinateMenu(null);
    setMode('browse');
  };

  const openCoordinateMenu = (event, mapRef) => {
    event.preventDefault();
    event.originalEvent.preventDefault();
    const labelContext = mapLabelContextAtPoint(mapRef, event);
    setCoordinateMenu({
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
      ...labelContext
    });
  };

  const handleBrowseMoveEnd = () => {
    if (browseMapRef.current) {
      setBrowseView(viewStateFromMap(browseMapRef.current));
    }
    clearTimeout(moveTimeoutRef.current);
    moveTimeoutRef.current = setTimeout(refreshVisibleSwims, 200);
  };

  const handleBrowseClick = (event) => {
    const routeId = event.features?.[0]?.properties?.id;
    if (routeId) {
      const swim = knownSwims.find((knownSwim) => knownSwim.id === routeId) || null;
      if (!swim) return;
      selectedSwimIdRef.current = swim.id;
      setSelectedSwim(swim);
      setSelectedAreaResearch(null);
      loadSelectedAreaResearch(swim.id);
    }
  };

  const addOrMoveDraftPoint = (event) => {
    if (!editAction || !draft) return;
    const point = new GeoPoint(event.lngLat.lat, event.lngLat.lng);
    const route = [...draft.route];

    if (editAction === 'add') {
      route.push(point);
      setSelectedPointIndex(route.length - 1);
    } else if (editAction === 'insert-before') {
      route.splice(selectedPointIndex, 0, point);
    } else {
      route[selectedPointIndex] = point;
    }

    setDraft({ ...draft, route });
    if (editAction !== 'add') setEditAction(null);
  };

  const deleteSelectedPoint = () => {
    if (selectedPointIndex === null || !draft) return;
    const route = draft.route.filter((_, index) => index !== selectedPointIndex);
    setDraft({ ...draft, route });
    setSelectedPointIndex(null);
    setEditAction(null);
  };

  const moveDraftPointByDrag = (index, event) => {
    if (!draft) return;
    const route = [...draft.route];
    route[index] = new GeoPoint(event.lngLat.lat, event.lngLat.lng);
    setDraft({ ...draft, route });
    setSelectedPointIndex(index);
    setEditAction(null);
  };

  const deleteKnownSwim = async () => {
    if (!selectedSwim) return;
    const confirmed = window.confirm(`Delete "${selectedSwim.name}"? This cannot be undone.`);
    if (!confirmed) return;

    setError(null);
    try {
      await deleteDoc(doc(db, 'KnownSwims', selectedSwim.id, 'research', 'area'));
      await deleteDoc(doc(db, 'KnownSwims', selectedSwim.id));
      setSelectedSwim(null);
    } catch (deleteError) {
      console.error('Error deleting known swim:', deleteError);
      setError('Unable to delete the known swim.');
    }
  };

  const loadUserRoutes = useCallback(async (map) => {
    if (!map || map.getZoom() < EDITOR_ZOOM) {
      setError('Zoom in before loading user routes.');
      return;
    }

    setLoadingUserRoutes(true);
    setError(null);
    try {
      const getRoutesInBounds = httpsCallable(functions, 'getRoutesInBounds');
      const result = await getRoutesInBounds({
        bounds: boundsFromMap(map),
        pinnedSwim: focusedLatestSwim
      });
      const routes = [...(result.data.routes || [])].sort(
        (first, second) => (Date.parse(second.startDate) || 0) - (Date.parse(first.startDate) || 0)
      );
      setUserRoutes(routes);
      setSelectedUserRouteIndex(0);
    } catch (loadError) {
      console.error('Error loading user routes:', loadError);
      setError(loadError.message || 'Unable to load user routes in this area.');
    } finally {
      setLoadingUserRoutes(false);
    }
  }, [focusedLatestSwim]);

  useEffect(() => {
    const map = browseMapRef.current;
    if (!pendingFocusedRouteLoad || mode !== 'browse' || !mapLoaded || !map) return;
    if (map.getZoom() < EDITOR_ZOOM) return;

    setPendingFocusedRouteLoad(false);
    void loadUserRoutes(map);
  }, [pendingFocusedRouteLoad, mode, mapLoaded, loadUserRoutes]);

  const userRouteFeatures = useMemo(() => ({
    type: 'FeatureCollection',
    features: userRoutes.filter((_, index) => index !== selectedUserRouteIndex).map((route) => ({
      type: 'Feature',
      properties: { id: route.id },
      geometry: { type: 'LineString', coordinates: route.coordinates }
    }))
  }), [userRoutes, selectedUserRouteIndex]);

  const selectedUserRouteFeature = useMemo(() => {
    const route = userRoutes[selectedUserRouteIndex];
    return route ? {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: { id: route.id }, geometry: { type: 'LineString', coordinates: route.coordinates } }]
    } : emptyFeatureCollection;
  }, [userRoutes, selectedUserRouteIndex]);

  const selectedUserRouteEndpoints = useMemo(() => {
    const route = userRoutes[selectedUserRouteIndex];
    if (!route?.coordinates || route.coordinates.length < 2) return emptyFeatureCollection;

    return {
      type: 'FeatureCollection',
      features: [
        { type: 'Feature', properties: { endpoint: 'start' }, geometry: { type: 'Point', coordinates: route.coordinates[0] } },
        { type: 'Feature', properties: { endpoint: 'finish' }, geometry: { type: 'Point', coordinates: route.coordinates.at(-1) } }
      ]
    };
  }, [userRoutes, selectedUserRouteIndex]);

  const cycleUserRoute = (direction) => {
    if (userRoutes.length === 0) return;
    setSelectedUserRouteIndex((index) => (index + direction + userRoutes.length) % userRoutes.length);
  };

  const selectorWheelRef = useRef(0);
  const selectorTouchStartRef = useRef(null);
  const handleSelectorWheel = (event) => {
    event.preventDefault();
    selectorWheelRef.current += event.deltaY;
    const steps = Math.trunc(Math.abs(selectorWheelRef.current) / 45);
    if (steps === 0) return;
    const direction = selectorWheelRef.current > 0 ? 1 : -1;
    selectorWheelRef.current -= direction * steps * 45;
    for (let index = 0; index < Math.min(steps, 5); index += 1) cycleUserRoute(direction);
  };

  const runAreaResearch = async (refresh = false, isManual = true, swim = draft) => {
    if (!swim?.id) return;
    const isActiveEditorSwim = () => activeEditorSwimIdRef.current === swim.id;
    if (isManual) setError(null);
    try {
      const result = await researchSwim(swim.id, refresh);
      if (isActiveEditorSwim()) {
        setAreaResearch(normalizeAreaResearch(result.areaResearch));
        if (!draftNameRef.current.trim() && result.appliedName) {
          setDraftName(result.appliedName);
          setDraft((current) => current?.id === swim.id ? { ...current, name: result.appliedName } : current);
        }
        setEditingAreaResearch(false);
      }
    } catch (researchError) {
      if (isManual && isActiveEditorSwim()) console.error('Unable to research swim area:', researchError);
    }
  };

  const loadAreaResearch = async (knownSwimId) => {
    const cachedSwim = knownSwims.find((swim) => swim.id === knownSwimId);
    if (activeEditorSwimIdRef.current !== knownSwimId) return;
    setAreaResearch(cachedSwim?.areaResearch || null);
    setLoadingAreaResearch(false);
  };

  const loadSelectedAreaResearch = async (knownSwimId) => {
    const cachedSwim = knownSwims.find((swim) => swim.id === knownSwimId);
    if (selectedSwimIdRef.current !== knownSwimId) return;
    setSelectedAreaResearch(cachedSwim?.areaResearch || null);
    setLoadingSelectedAreaResearch(false);
  };

  const updateAreaResearch = (field, value) => {
    setAreaResearch((current) => ({ ...(current || emptyAreaResearch), [field]: value }));
  };

  const saveAreaResearch = async () => {
    if (!draft?.id || !areaResearch) return;
    setSavingAreaResearch(true);
    setError(null);
    try {
      const researchToSave = editableAreaResearch(areaResearch);
      const savedResearch = { ...researchToSave, editedManually: true };
      await setDoc(doc(db, 'KnownSwims', draft.id, 'research', 'area'), {
        ...savedResearch,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setAreaResearch((current) => ({ ...current, ...savedResearch }));
      setEditingAreaResearch(false);
    } catch (saveError) {
      console.error('Unable to save swim area research:', saveError);
      setError('Unable to save swim area research.');
    } finally {
      setSavingAreaResearch(false);
    }
  };

  const draftFeature = useMemo(() => {
    if (!draft || draft.route.length < 2) return emptyFeatureCollection;
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: draft.route.map((point) => [point.longitude, point.latitude])
        }
      }]
    };
  }, [draft]);

  const draftDistanceInMeters = useMemo(() => {
    if (!draft) return 0;
    return calculateRouteDistanceMeters(draft.route) * (draft.outAndBack ? 2 : 1);
  }, [draft]);

  useEffect(() => {
    if (mode !== 'edit') {
      fittedDraftIdRef.current = null;
      return;
    }
    if (!draft?.id || !draft.route.length || !editorMapRef.current) return;
    const draftId = draft.id;
    if (fittedDraftIdRef.current === draftId) return;

    const bounds = new mapboxgl.LngLatBounds();
    draft.route.forEach((point) => bounds.extend([point.longitude, point.latitude]));
    editorMapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 0 });
    fittedDraftIdRef.current = draftId;
  }, [mode, draft]);

  const saveDraft = async () => {
    if (!draft || draft.route.length < 2) {
      setError('Add at least two route points before saving.');
      return;
    }

    setSaving(true);
    setError(null);
    const data = {
      name: draftName.trim(),
      outAndBack: Boolean(draft.outAndBack),
      strictlyOneWay: Boolean(draft.strictlyOneWay),
      route: draft.route,
      geoBoundingBox: calculateBoundingBox(draft.route),
      distanceInMeters: draftDistanceInMeters,
      updatedAt: serverTimestamp()
    };

    try {
      let savedId = draft.id;
      if (draft.id) {
        await updateDoc(doc(db, 'KnownSwims', draft.id), data);
      } else {
        const createdSwim = await addDoc(collection(db, 'KnownSwims'), {
          ...data,
          createdAt: serverTimestamp()
        });
        savedId = createdSwim.id;
      }
      const savedDraft = {
        ...draft,
        id: savedId,
        name: data.name,
        outAndBack: data.outAndBack,
        strictlyOneWay: data.strictlyOneWay,
        geoBoundingBox: data.geoBoundingBox,
        distanceInMeters: data.distanceInMeters
      };
      activeEditorSwimIdRef.current = savedId;
      setDraft(savedDraft);
      void runAreaResearch(false, false, savedDraft);
    } catch (saveError) {
      console.error('Error saving known swim:', saveError);
      setError('Unable to save the known swim.');
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="known-swims-page">
        <Map
          key="known-swims-map"
          ref={editorMapRef}
          initialViewState={editorView}
          mapStyle={EDITOR_STYLE}
          mapboxAccessToken={mapboxToken}
          style={{ width: '100%', height: '100%' }}
          cursor={editAction ? 'crosshair' : 'grab'}
          onClick={addOrMoveDraftPoint}
          onContextMenu={(event) => openCoordinateMenu(event, editorMapRef)}
          onMoveEnd={() => setEditorZoom(editorMapRef.current?.getZoom() || 3)}
          onLoad={() => setMapLoaded(true)}
        >
          {mapLoaded && (
            <>
              <Source id="recent-swim-routes" type="geojson" data={recentRouteFeatures}>
                <Layer id="recent-swim-route-lines" type="line" layout={lineLayout} paint={{ 'line-color': '#22d3ee', 'line-width': 1.5, 'line-opacity': 0.8 }} />
              </Source>
              <Source id="user-routes" type="geojson" data={userRouteFeatures}>
                <Layer id="user-route-lines" type="line" layout={lineLayout} paint={{ 'line-color': '#ffffff', 'line-width': 1.5, 'line-opacity': 0.75 }} />
              </Source>
              <Source id="known-swim-draft" type="geojson" data={draftFeature}>
                <Layer id="known-swim-draft-line" type="line" layout={lineLayout} paint={{ 'line-color': '#007bff', 'line-width': 4 }} />
              </Source>
              <Source id="selected-user-route" type="geojson" data={selectedUserRouteFeature}>
                <Layer id="selected-user-route-line" type="line" layout={lineLayout} paint={{ 'line-color': '#f59e0b', 'line-width': 3, 'line-opacity': 0.95 }} />
              </Source>
              <Source id="selected-user-route-endpoints" type="geojson" data={selectedUserRouteEndpoints}>
                <Layer id="selected-user-route-endpoint-dots" type="circle" paint={{ 'circle-radius': 5, 'circle-color': ['match', ['get', 'endpoint'], 'start', '#22c55e', '#ef4444'], 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5 }} />
              </Source>
            </>
          )}
          {draft?.route.map((point, index) => (
            <Marker
              key={`${point.latitude}-${point.longitude}-${index}`}
              longitude={point.longitude}
              latitude={point.latitude}
              draggable
              onDragStart={() => {
                setSelectedPointIndex(index);
                setEditAction(null);
              }}
              onDragEnd={(event) => moveDraftPointByDrag(index, event)}
            >
              <button
                className={`known-swim-point ${selectedPointIndex === index ? 'selected' : ''}`}
                aria-label={`Select route point ${index + 1}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedPointIndex(index);
                  setEditAction(null);
                }}
              />
            </Marker>
          ))}
          <CoordinateMenu coordinate={coordinateMenu} onClose={() => setCoordinateMenu(null)} onError={setError} />
        </Map>

        <div className="known-swims-editor-panel">
          <button className="known-swims-close" onClick={returnToBrowse} aria-label="Close editor">x</button>
          <h2>{draft?.id ? 'Edit Known Swim' : 'New Known Swim'}</h2>
          <label htmlFor="known-swim-name">Route name</label>
          <input id="known-swim-name" value={draftName} onChange={(event) => setDraftName(event.target.value)} />
          <label className="known-swims-checkbox" htmlFor="known-swim-out-and-back">
            <input
              id="known-swim-out-and-back"
              type="checkbox"
              checked={Boolean(draft?.outAndBack)}
              onChange={(event) => setDraft({
                ...draft,
                outAndBack: event.target.checked,
                strictlyOneWay: event.target.checked ? false : draft.strictlyOneWay
              })}
            />
            Out and back
          </label>
          <label className="known-swims-checkbox" htmlFor="known-swim-strictly-one-way">
            <input
              id="known-swim-strictly-one-way"
              type="checkbox"
              checked={Boolean(draft?.strictlyOneWay)}
              onChange={(event) => setDraft({
                ...draft,
                strictlyOneWay: event.target.checked,
                outAndBack: event.target.checked ? false : draft.outAndBack
              })}
            />
            Strictly one way
          </label>
          <p>{editAction === 'add' ? 'Click the map to add a point.' : editAction === 'insert-before' ? 'Click the map to insert a point before the selected point.' : editAction === 'move' ? 'Click the map to move the selected point.' : `${draft?.route.length || 0} points`}</p>
          <p className="known-swims-distance">Distance: {formatDistance(draftDistanceInMeters)}</p>
          <AreaResearchPanel
            research={areaResearch}
            loading={loadingAreaResearch}
            editing={editingAreaResearch}
            researching={researching}
            saving={savingAreaResearch}
            canResearch={Boolean(draft?.id)}
            onResearch={() => runAreaResearch(Boolean(areaResearch))}
            onEdit={() => {
              setAreaResearch((current) => current || { ...emptyAreaResearch });
              setEditingAreaResearch(true);
            }}
            onCancel={() => {
              setEditingAreaResearch(false);
              if (draft?.id) loadAreaResearch(draft.id);
            }}
            onSave={saveAreaResearch}
            onChange={updateAreaResearch}
          />
          <div className="known-swims-button-row">
            <button onClick={() => setEditAction(editAction === 'add' ? null : 'add')}>
              {editAction === 'add' ? 'Stop adding points' : 'Add points'}
            </button>
            <button onClick={() => setEditAction('insert-before')} disabled={selectedPointIndex === null}>Insert Point Before</button>
            <button onClick={() => setEditAction('move')} disabled={selectedPointIndex === null}>Move point</button>
            <button onClick={deleteSelectedPoint} disabled={selectedPointIndex === null}>Delete point</button>
          </div>
          <div className="known-swims-button-row">
            <button onClick={saveDraft} disabled={saving}>{saving ? 'Saving...' : 'Save route'}</button>
            <button className="secondary" onClick={returnToBrowse}>Cancel</button>
          </div>
        </div>

        <button className="known-swims-load-routes" onClick={() => loadUserRoutes(editorMapRef.current)} disabled={loadingUserRoutes || editorZoom < EDITOR_ZOOM}>
          {loadingUserRoutes ? 'Loading routes...' : editorZoom < EDITOR_ZOOM ? `Zoom to ${EDITOR_ZOOM} to load routes` : 'Load user routes'}
        </button>
        <RouteSelector
          routeCount={userRoutes.length}
          selectedIndex={selectedUserRouteIndex}
          route={userRoutes[selectedUserRouteIndex]}
          onPrevious={() => cycleUserRoute(-1)}
          onNext={() => cycleUserRoute(1)}
          onWheel={handleSelectorWheel}
          touchStartRef={selectorTouchStartRef}
          onSwipe={cycleUserRoute}
        />
        {(error || activeResearchError) && <div className="known-swims-error">{error || activeResearchError}</div>}
      </div>
    );
  }

  return (
    <div className="known-swims-page">
      <Map
        key="known-swims-map"
        ref={browseMapRef}
        initialViewState={browseView}
        mapStyle={EDITOR_STYLE}
        mapboxAccessToken={mapboxToken}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['known-swim-routes']}
        onClick={handleBrowseClick}
        onContextMenu={(event) => openCoordinateMenu(event, browseMapRef)}
        onMoveEnd={handleBrowseMoveEnd}
        onLoad={() => setMapLoaded(true)}
      >
        {mapLoaded && !selectedSwim && (
          <Source id="known-swims" type="geojson" data={knownRouteFeatures}>
            <Layer id="known-swim-routes" type="line" layout={lineLayout} paint={{ 'line-color': '#007bff', 'line-width': 4 }} />
          </Source>
        )}
        {mapLoaded && (
          <Source id="browse-recent-swim-routes" type="geojson" data={recentRouteFeatures}>
            <Layer id="browse-recent-swim-route-lines" type="line" layout={lineLayout} paint={{ 'line-color': '#22d3ee', 'line-width': 1.5, 'line-opacity': 0.8 }} />
          </Source>
        )}
        {mapLoaded && selectedSwim?.route?.length >= 2 && (
          <Source id="selected-known-swim" type="geojson" data={routeFeature(selectedSwim)}>
            <Layer id="selected-known-swim-route" type="line" layout={lineLayout} paint={{ 'line-color': '#007bff', 'line-width': 5 }} />
          </Source>
        )}
        {mapLoaded && (
          <Source id="browse-user-routes" type="geojson" data={userRouteFeatures}>
            <Layer id="browse-user-route-lines" type="line" layout={lineLayout} paint={{ 'line-color': '#ffffff', 'line-width': 1.5, 'line-opacity': 0.75 }} />
          </Source>
        )}
        {mapLoaded && (
          <Source id="browse-selected-user-route" type="geojson" data={selectedUserRouteFeature}>
            <Layer id="browse-selected-user-route-line" type="line" layout={lineLayout} paint={{ 'line-color': '#f59e0b', 'line-width': 3, 'line-opacity': 0.95 }} />
          </Source>
        )}
        {mapLoaded && (
          <Source id="browse-selected-user-route-endpoints" type="geojson" data={selectedUserRouteEndpoints}>
            <Layer id="browse-selected-user-route-endpoint-dots" type="circle" paint={{ 'circle-radius': 5, 'circle-color': ['match', ['get', 'endpoint'], 'start', '#22c55e', '#ef4444'], 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5 }} />
          </Source>
        )}
        {mapLoaded && (
          <Source id="focused-latest-swim-route" type="geojson" data={focusedLatestRouteFeature}>
            <Layer id="focused-latest-swim-route-line" type="line" layout={lineLayout} paint={{ 'line-color': '#f59e0b', 'line-width': 4, 'line-opacity': 1 }} />
          </Source>
        )}
        <CoordinateMenu coordinate={coordinateMenu} onClose={() => setCoordinateMenu(null)} onError={setError} />
      </Map>

      <button className="known-swims-new" onClick={() => startEditing()}>New known swim</button>
      <button
        className="known-swims-load-routes"
        onClick={() => loadUserRoutes(browseMapRef.current)}
        disabled={loadingUserRoutes || browseView.zoom < EDITOR_ZOOM}
      >
        {loadingUserRoutes ? 'Loading routes...' : browseView.zoom < EDITOR_ZOOM ? `Zoom to ${EDITOR_ZOOM} to load routes` : 'Load user routes'}
      </button>
      {loadingKnownSwims && <div className="known-swims-status">Loading known swims...</div>}
      {!loadingKnownSwims && browseMapRef.current?.getZoom() < BROWSE_ZOOM && <div className="known-swims-status">Zoom in to view known swims.</div>}
      {(error || knownSwimsError || activeResearchError) && <div className="known-swims-error">{error || knownSwimsError || activeResearchError}</div>}
      <RouteSelector
        routeCount={userRoutes.length}
        selectedIndex={selectedUserRouteIndex}
        route={userRoutes[selectedUserRouteIndex]}
        onPrevious={() => cycleUserRoute(-1)}
        onNext={() => cycleUserRoute(1)}
        onWheel={handleSelectorWheel}
        touchStartRef={selectorTouchStartRef}
        onSwipe={cycleUserRoute}
      />
      {selectedSwim && (
        <div className="known-swims-overlay">
          <button className="known-swims-close" onClick={closeOverlay} aria-label="Close selected route">x</button>
          <h2>{selectedSwim.name}</h2>
          <p className="known-swims-distance">
            Distance: {formatDistance(selectedSwim.distanceInMeters ?? calculateRouteDistanceMeters(selectedSwim.route) * (selectedSwim.outAndBack ? 2 : 1))}
          </p>
          <AreaResearchPanel
            research={selectedAreaResearch}
            loading={loadingSelectedAreaResearch}
            canResearch
            readOnly
          />
          <div className="known-swims-overlay-actions">
            <button onClick={() => startEditing(selectedSwim)}>Edit</button>
            <button className="known-swims-delete" onClick={deleteKnownSwim}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
};

const CoordinateMenu = ({ coordinate, onClose, onError }) => {
  if (!coordinate) return null;
  const coordinateText = formatCoordinate(coordinate);
  const searchText = `open water swimming at ${coordinateText}`;
  const placeName = coordinate.placeName;
  const placeSearchText = placeName ? `open water swimming at ${placeName}` : null;
  const nearbyPlaceSearchText = placeName ? `${placeSearchText} near ${coordinateText}` : null;
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (copyError) {
      console.error('Unable to copy map coordinate:', copyError);
      onError('Unable to copy the map coordinate.');
    }
  };

  return (
    <Popup
      longitude={coordinate.longitude}
      latitude={coordinate.latitude}
      closeOnClick={false}
      onClose={onClose}
      maxWidth="260px"
      className="known-swims-coordinate-popup"
    >
      <div className="known-swims-coordinate-menu">
        {placeName && (
          <>
            <strong>{placeName}</strong>
            <button onClick={() => copyText(placeName)}>Copy place name</button>
            <button onClick={() => window.open(aiModeSearchUrl(placeSearchText), '_blank', 'noopener,noreferrer')}>
              Search for place
            </button>
            <button onClick={() => window.open(aiModeSearchUrl(nearbyPlaceSearchText), '_blank', 'noopener,noreferrer')}>
              Search for place near coordinate
            </button>
          </>
        )}
        <strong>{coordinateText}</strong>
        <button onClick={() => copyText(coordinateText)}>Copy coordinate</button>
        <button onClick={() => copyText(searchText)}>Copy search text</button>
        <button onClick={() => window.open(aiModeSearchUrl(searchText), '_blank', 'noopener,noreferrer')}>
          Search
        </button>
      </div>
    </Popup>
  );
};

const RouteSelector = ({ routeCount, selectedIndex, route, onPrevious, onNext, onWheel, touchStartRef, onSwipe }) => {
  const [showInfo, setShowInfo] = useState(false);
  if (routeCount === 0) return null;
  const routeDistance = `${Math.round(calculateCoordinateRouteDistanceMeters(route?.coordinates || []))}m`;
  const routeMonthYear = formatRouteMonthYear(route?.startDate);
  return (
    <div
      className="known-swims-route-selector"
      onWheel={onWheel}
      onTouchStart={(event) => { touchStartRef.current = event.touches[0].clientY; }}
      onTouchEnd={(event) => {
        const start = touchStartRef.current;
        const end = event.changedTouches[0].clientY;
        if (start !== null && Math.abs(end - start) >= 35) onSwipe(end < start ? 1 : -1);
        touchStartRef.current = null;
      }}
    >
      <div className="known-swims-route-summary">
        <span>Route {selectedIndex + 1} of {routeCount}</span>
        <span className="known-swims-route-details">{routeMonthYear ? `${routeDistance} - ${routeMonthYear}` : routeDistance}</span>
      </div>
      <div>
        <button onClick={onPrevious} aria-label="Previous loaded route">Previous</button>
        <button onClick={onNext} aria-label="Next loaded route">Next</button>
        <button
          className="known-swims-route-info-toggle"
          onClick={() => setShowInfo((current) => !current)}
          aria-label="Toggle selected route information"
          aria-expanded={showInfo}
        >
          i
        </button>
      </div>
      {showInfo && (
        <dl className="known-swims-route-info">
          <div><dt>Start</dt><dd>{route?.startDate ? new Date(route.startDate).toLocaleString() : 'Unavailable'}</dd></div>
          <div><dt>User ID</dt><dd>{route?.userID || 'Unavailable'}</dd></div>
        </dl>
      )}
    </div>
  );
};

export default KnownSwimsPage;
